<template>
  <BaseModal
    v-model="showModal"
    title="Nouveau paiement"
    size="2xl"
    :closable="!isSubmitting"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white shadow-lg">
          <BaseIcon name="credit-card" :size="24" />
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Nouveau paiement</h2>
          <p class="text-sm text-gray-500">Enregistrer un paiement pour cette facture</p>
        </div>
      </div>
    </template>

    <form @submit.prevent="submitPayment" class="space-y-6">
      <!-- Résumé de la facture -->
      <div class="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Facture</span>
          <span class="text-sm font-semibold text-gray-900 font-mono">{{ invoiceNumber }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Montant total</span>
          <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(totalAmount) }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Déjà payé</span>
          <span class="text-sm font-semibold text-green-600">{{ formatCurrency(paidAmount) }}</span>
        </div>
        <div class="flex justify-between items-center pt-3 mt-2 bg-primary/10 -mx-4 -mb-4 px-4 pb-4 rounded-b-xl">
          <span class="text-sm font-medium text-gray-700">Reste à payer</span>
          <span class="text-base font-bold text-primary">{{ formatCurrency(remainingAmount) }}</span>
        </div>
      </div>

      <!-- Détails du paiement -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="dollar-sign" :size="18" />
          <span>Détails du paiement</span>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Montant <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                v-model.number="form.amount"
                type="number"
                :max="remainingAmount"
                min="1"
                step="1"
                class="w-full px-4 py-3 pr-16 text-base border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
                :class="{ 'border-red-500': validationErrors.amount }"
                :placeholder="`Maximum: ${formatCurrency(remainingAmount)}`"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">FCFA</span>
            </div>
            <span v-if="validationErrors.amount" class="text-xs text-red-500 mt-1 block">{{ validationErrors.amount }}</span>
            <BaseButton
              type="button"
              class="w-full mt-2 py-2 px-4 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary hover:text-white transition-all"
              @click="form.amount = remainingAmount"
            >
              Payer le solde complet ({{ formatCurrency(remainingAmount) }})
            </BaseButton>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseSelect
              v-model="form.payment_type"
              label="Mode de paiement"
              :options="paymentTypeOptions"
              :error="validationErrors.payment_type"
              required
            />
            <BaseInput
              v-model="form.payment_date"
              type="date"
              label="Date du paiement"
              :error="validationErrors.payment_date"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Notes (optionnel)</label>
            <textarea
              v-model="form.notes"
              rows="2"
              class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all resize-none"
              placeholder="Référence, détails supplémentaires..."
            ></textarea>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <span class="text-xs text-gray-500">* Champs obligatoires</span>
        <div class="flex gap-3">
          <BaseButton variant="ghost" :disabled="isSubmitting" @click="closeModal">
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isSubmitting"
            :disabled="!isFormValid"
            @click="submitPayment"
          >
            <template #icon-left>
              <BaseIcon name="check" :size="18" />
            </template>
            Enregistrer le paiement
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { saleInvoiceService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseInput, BaseSelect, BaseIcon } from '@/components/base'

export default defineComponent({
  name: 'MakeSaleInvoicePaymentModal',
  components: {
    BaseModal,
    BaseButton,
    BaseInput,
    BaseSelect,
    BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      type: [Number, String],
      required: true
    },
    invoiceNumber: {
      type: String,
      default: ''
    },
    totalAmount: {
      type: Number,
      default: 0
    },
    paidAmount: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'payment-added'],
  setup(props, { emit }) {
    const toastStore = useToastStore()
    const isSubmitting = ref(false)
    const validationErrors = ref({})

    const paymentTypeOptions = [
      { value: 'cash', label: 'Espèces' },
      { value: 'card', label: 'Carte bancaire' },
      { value: 'mobile_money', label: 'Mobile Money' },
      { value: 'bank_transfer', label: 'Virement bancaire' },
      { value: 'check', label: 'Chèque' }
    ]

    const form = ref({
      amount: null,
      payment_type: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    })

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close')
        }
      }
    })

    const remainingAmount = computed(() => {
      return (props.totalAmount || 0) - (props.paidAmount || 0)
    })

    const isFormValid = computed(() => {
      return form.value.amount > 0 &&
        form.value.amount <= remainingAmount.value &&
        form.value.payment_type &&
        form.value.payment_date
    })

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const resetForm = () => {
      form.value = {
        amount: remainingAmount.value,
        payment_type: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      }
      validationErrors.value = {}
    }

    const validateForm = () => {
      validationErrors.value = {}
      let isValid = true

      if (!form.value.amount || form.value.amount <= 0) {
        validationErrors.value.amount = 'Le montant doit être supérieur à 0'
        isValid = false
      } else if (form.value.amount > remainingAmount.value) {
        validationErrors.value.amount = `Le montant ne peut pas dépasser ${formatCurrency(remainingAmount.value)}`
        isValid = false
      }

      if (!form.value.payment_type) {
        validationErrors.value.payment_type = 'Le mode de paiement est requis'
        isValid = false
      }

      if (!form.value.payment_date) {
        validationErrors.value.payment_date = 'La date est requise'
        isValid = false
      }

      return isValid
    }

    const submitPayment = async () => {
      if (!validateForm()) return

      try {
        isSubmitting.value = true
        await saleInvoiceService.addPaymentToInvoice(props.invoiceId, {
          amount: form.value.amount,
          payment_type: form.value.payment_type,
          payment_date: form.value.payment_date,
          notes: form.value.notes || undefined
        })

        toastStore.addToast({
          type: 'success',
          message: 'Paiement enregistré avec succès',
          duration: 4000
        })

        emit('payment-added')
        closeModal()
      } catch (error) {
        console.error('Erreur:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement',
          duration: 4000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        resetForm()
      }
    })

    return {
      showModal,
      form,
      isSubmitting,
      validationErrors,
      paymentTypeOptions,
      remainingAmount,
      isFormValid,
      formatCurrency,
      submitPayment,
      closeModal
    }
  }
})
</script>
